import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getAvatarUrl } from "../../avatars";
import { useConfirm } from "../../components/ui/Confirm";
import { useToast } from "../../components/ui/Toast";
import ImmersiveTutorial from "../../components/ui/ImmersiveTutorial";
import SlotMachineAnimation from "./SlotMachineAnimation";
import { TUTORIAL_STEPS } from "../../data/origami/tutorialSteps";
import FakeGameInterface from "../../games/origami/components/ui/FakeGameInterface";

const API = process.env.REACT_APP_API_URL;

function WaitingRoom() {
  const { code } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();

  const [players, setPlayers] = useState([]);
  const [error, setError] = useState(null);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [onboardingDone, setOnboardingDone] = useState(false);

  const [proposals, setProposals] = useState({});
  const [votes, setVotes] = useState({});
  const [finalizeResult, setFinalizeResult] = useState(null);
  const [showTiebreakerAnimation, setShowTiebreakerAnimation] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);

  const currentPlayerId = Number(sessionStorage.getItem(`playerId:${code}`));
  const onboardKey = `onboardingDone:${code}:${currentPlayerId || "x"}`;

  useEffect(() => {
    if (!currentPlayerId) {
      navigate(`/play/${code}`);
      return;
    }

    // onboarding local (par joueur)
    const done = sessionStorage.getItem(onboardKey) === "1";
    setOnboardingDone(done);
    if (!done) setOnboardingOpen(true);

    const fetchPlayers = async () => {
      try {
        const res = await axios.get(`${API}/api/sessions/${code}/players`);
        setPlayers(res.data.players);

        // DÉTECTER si un finalize a eu lieu
        const finalizeRes = await axios.get(`${API}/api/sessions/${code}/team-name/finalize-result`);
        const { result, sessionStatus } = finalizeRes.data;

        // Si un résultat existe et qu'on ne l'a pas encore affiché
        if (result && !finalizeResult && !isLaunching) {
          setIsLaunching(true);
          setFinalizeResult(result);

          // Si tiebreaker → afficher l'animation complète
          if (result.method === "tiebreaker" || result.method === "random_pick") {
            setShowTiebreakerAnimation(true);
            await new Promise(resolve => setTimeout(resolve, 8000));
            setShowTiebreakerAnimation(false);
          } else {
            // Autres méthodes → attendre 2s pour voir le toast
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }

        // Redirection quand la partie est lancée
        if (sessionStatus === "in_progress") {
          navigate(`/game/${code}`);
        }
        if (sessionStatus === "used") {
          navigate(`/scoreboard/${code}`);
        }
      } catch (err) {
        setError("Impossible de récupérer les joueurs");
      }
    };

    fetchPlayers();
    const itv = setInterval(fetchPlayers, 1000);
    return () => clearInterval(itv);
  }, [code, currentPlayerId, navigate, onboardKey, finalizeResult, isLaunching]);

  useEffect(() => {
    if (!currentPlayerId) return;

    const fetchProposals = async () => {
      try {
        const res = await axios.get(`${API}/api/sessions/${code}/team-name/proposals`);
        setProposals(res.data.proposals || {});
        setVotes(res.data.votes || {});
      } catch (err) {
        console.error("Erreur fetch proposals", err);
      }
    };

    fetchProposals();
    const itv = setInterval(fetchProposals, 2000); // Polling toutes les 2s
    return () => clearInterval(itv);
  }, [code, currentPlayerId]);

  const toggleReady = async () => {
    try {
      await axios.patch(`${API}/api/players/${currentPlayerId}/ready`, {
        isReady: !players.find((p) => p.id === currentPlayerId)?.isReady,
      });
      const res = await axios.get(`${API}/api/sessions/${code}/players`);
      setPlayers(res.data.players);
    } catch (err) {
      // no-op
    }
  };

  const handleVote = async (votedFor) => {
    try {
      await axios.post(`${API}/api/sessions/${code}/team-name/vote`, {
        playerId: currentPlayerId,
        votedFor, // playerId, "random", ou null
      });

      // Refresh immédiat
      const res = await axios.get(`${API}/api/sessions/${code}/team-name/proposals`);
      setVotes(res.data.votes || {});
    } catch (err) {
      console.error("Erreur vote", err);
    }
  };

  const launchGame = async () => {
    const ok = await confirm({
      title: "Lancer la partie ?",
      message: "Cela utilisera définitivement le code et la partie démarrera pour tout le monde. Vérifiez bien que tout le monde est présent et prêt.",
      confirmText: "Lancer",
      cancelText: "Annuler",
      danger: true,
    });

    if (!ok) return;

    setIsLaunching(true);

    try {
      // 1. Finaliser le nom d'équipe (stocke le résultat en base)
      const finalizeRes = await axios.post(`${API}/api/sessions/${code}/team-name/finalize`);
      const result = finalizeRes.data;

      // 2. Afficher l'animation LOCALEMENT pour celui qui lance
      setFinalizeResult(result);

      let waitTime = 2000; // Par défaut 2s (pour toast)

      // Animation pour tiebreaker ET random_pick
      if (result.method === "tiebreaker" || result.method === "random_pick") {
        setShowTiebreakerAnimation(true);
        waitTime = 8000; // 8s pour l'animation complète

        await new Promise(resolve => setTimeout(resolve, waitTime));

        setShowTiebreakerAnimation(false);
      } else {
        // Pour les autres méthodes, juste attendre pour le toast
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }

      // 3. Lancer la partie
      await axios.post(`${API}/api/sessions/${code}/launch`);

      // La redirection sera gérée par le useEffect
    } catch (err) {
      setIsLaunching(false);
      toast.error(err.response?.data?.message || "Impossible de lancer la partie", { title: "Erreur" });
    }
  };

  const completeOnboarding = () => {
    sessionStorage.setItem(onboardKey, "1");
    setOnboardingDone(true);
    setOnboardingOpen(false);
  };

  const me = players.find((p) => p.id === currentPlayerId);
  const readyCount = players.filter((p) => p.isReady).length;
  const allReady =
    players.length >= 4 && players.length <= 6 && players.every((p) => p.isReady);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 text-white flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-3xl">
        <div className="bg-white/10 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-white">Salle d’attente</h1>
            <span className="text-xs px-2 py-1 rounded-full bg-white/10 border border-white/10 text-indigo-300 font-mono uppercase tracking-wider">
              Code session - {code}
            </span>
          </div>

          <p className="text-gray-400 text-sm mt-1">
            En attente de tous les joueurs…{" "}
            <span className="text-indigo-300">{readyCount}</span> prêt(s) / {players.length}
          </p>
          <div className="mt-4 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-slate-200">
            <p className="text-gray-300">
              1) <span className="text-white font-semibold">Chacun clique “Je suis prêt(e)”</span> dès qu’il est là.
            </p>
            <p className="text-gray-300">
              2) Quand <span className="text-white font-semibold">tout le monde est prêt</span>, une personne clique
              {" "}<span className="text-emerald-300 font-semibold">“Lancer la partie”</span>.
            </p>
            <p className="text-gray-300">
              3) <span className="text-white font-semibold">Attendez</span> tous les joueurs, aucun retour en arrière n'est possible une fois la partie lancée.
            </p>
          </div>


          {error && (
            <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm px-3 py-2">
              {error}
            </div>
          )}

          {/* Section propositions + vote */}
          {Object.keys(proposals).length > 0 && (
            <div className="mt-4 rounded-xl border border-indigo-400/30 bg-indigo-500/10 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-indigo-100">
                  🏷️ Noms d'équipe proposés
                </h3>
                <span className="text-xs text-indigo-300">
                  {Object.keys(votes).length} vote{Object.keys(votes).length > 1 ? "s" : ""}
                </span>
              </div>

              <p className="text-xs text-indigo-200/80 mb-3">
                Votez pour votre préféré (optionnel). Le nom sera choisi au lancement.
              </p>

              <div className="space-y-2">
                {/* Propositions */}
                {Object.entries(proposals).map(([playerId, name]) => {
                  const proposer = players.find((p) => p.id === Number(playerId));
                  const voteCount = Object.values(votes).filter((v) => v === playerId).length;
                  const isMyVote = votes[String(currentPlayerId)] === playerId;

                  return (
                    <button
                      key={playerId}
                      onClick={() => handleVote(playerId)}
                      className={[
                        "w-full text-left rounded-lg border px-3 py-2 transition",
                        isMyVote
                          ? "border-emerald-400 bg-emerald-500/20 ring-1 ring-emerald-400/50"
                          : "border-white/10 bg-white/5 hover:bg-white/10",
                      ].join(" ")}
                    >
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-white truncate">{name}</p>
                          <p className="text-xs text-slate-400">
                            Proposé par {proposer?.pseudo || "?"}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {voteCount > 0 && (
                            <span className="px-2 py-0.5 rounded-full bg-white/10 text-xs text-slate-300">
                              {voteCount}
                            </span>
                          )}
                          {isMyVote && <span className="text-lg">✓</span>}
                        </div>
                      </div>
                    </button>
                  );
                })}

                {/* Option aléatoire */}
                <button
                  onClick={() => handleVote("random")}
                  className={[
                    "w-full text-left rounded-lg border px-3 py-2 transition",
                    votes[String(currentPlayerId)] === "random"
                      ? "border-amber-400 bg-amber-500/20 ring-1 ring-amber-400/50"
                      : "border-white/10 bg-white/5 hover:bg-white/10",
                  ].join(" ")}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-white">🎲 Choisir au hasard</p>
                      <p className="text-xs text-slate-400">Tirage parmi les propositions</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Afficher le nombre de votes "random" */}
                      {Object.values(votes).filter((v) => v === "random").length > 0 && (
                        <span className="px-2 py-0.5 rounded-full bg-white/10 text-xs text-slate-300">
                          {Object.values(votes).filter((v) => v === "random").length}
                        </span>
                      )}
                      {votes[String(currentPlayerId)] === "random" && (
                        <span className="text-lg">✓</span>
                      )}
                    </div>
                  </div>
                </button>

                {/* Retirer mon vote */}
                {votes[String(currentPlayerId)] && (
                  <button
                    onClick={() => handleVote(null)}
                    className="w-full text-center rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 px-3 py-1.5 text-xs text-slate-300 transition"
                  >
                    Retirer mon vote
                  </button>
                )}
              </div>

              <p className="mt-3 text-xs text-slate-400">
                💡 Pas d'obligation de voter : le nom sera choisi automatiquement au lancement.
              </p>
            </div>
          )}

          {/* Pas de propositions */}
          {Object.keys(proposals).length === 0 && (
            <div className="mt-4 rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-xs text-slate-300">
              <p>
                💡 Si aucun nom d'équipe n'est proposé, un nom d'équipe sera généré automatiquement.
              </p>
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-white/10 my-4" />

          {/* Liste des joueurs */}
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {players.map((p) => {
              const isMe = p.id === currentPlayerId;
              const avatarUrl = p.avatar ? getAvatarUrl(p.avatar) : null;

              return (
                <div
                  key={p.id}
                  className={`flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 ${isMe ? "ring-1 ring-indigo-500/50" : ""
                    }`}
                >
                  <div className="flex items-center gap-3">
                    {/* Avatar / fallback initial */}
                    <div className="h-10 w-10 rounded-full overflow-hidden bg-indigo-500/20 flex items-center justify-center font-semibold">
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt={p.pseudo}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <span className="text-sm">
                          {p.pseudo?.slice(0, 1)?.toUpperCase() || "?"}
                        </span>
                      )}
                    </div>

                    <div>
                      <div className="font-semibold">
                        {p.pseudo}{" "}
                        {isMe && (
                          <span className="text-indigo-300 text-xs">(vous)</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400">
                        {p.isReady ? "Prêt" : "En attente…"}
                      </div>
                    </div>
                  </div>

                  <span
                    className={`text-xs px-2 py-1 rounded-full ${p.isReady
                      ? "bg-green-500/20 text-green-300 border border-green-500/30"
                      : "bg-yellow-500/20 text-yellow-300 border border-yellow-500/30"
                      }`}
                  >
                    {p.isReady ? "✅ prêt" : "⏳ en attente"}
                  </span>
                </div>
              );
            })}

            {players.length === 0 && (
              <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-6 text-center text-gray-400">
                En attente des premiers joueurs…
              </div>
            )}
          </div>

          {/* Boutons bas */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            {me && (
              <button
                onClick={toggleReady}
                disabled={!onboardingDone}
                className={`flex-1 py-3 rounded-lg font-semibold transition ${!onboardingDone
                  ? "bg-gray-800 opacity-60 cursor-not-allowed"
                  : me.isReady
                    ? "bg-gray-800 hover:bg-gray-700"
                    : "bg-indigo-600 hover:bg-indigo-500"
                  }`}
                title={!onboardingDone ? "Validez le tuto avant de vous mettre prêt." : undefined}
              >
                {!onboardingDone
                  ? "📘 Voir le tuto pour continuer"
                  : me.isReady
                    ? "Je ne suis plus prêt(e)"
                    : "Je suis prêt(e)"}
              </button>
            )}

            <button
              type="button"
              onClick={() => setOnboardingOpen(true)}
              className="sm:w-auto py-3 px-4 rounded-lg font-semibold bg-white/5 border border-white/10 hover:bg-white/10 transition"
            >
              📘 Tuto
            </button>

            {allReady ? (
              <button
                onClick={launchGame}
                disabled={isLaunching}
                className={[
                  "flex-1 py-3 rounded-lg font-semibold transition",
                  isLaunching
                    ? "bg-gray-700 opacity-60 cursor-not-allowed"
                    : "bg-emerald-600 hover:bg-emerald-500"
                ].join(" ")}
              >
                {isLaunching ? "⏳ Lancement en cours..." : "🚀 Lancer la partie"}
              </button>
            ) : (
              <button
                disabled
                className="flex-1 py-3 bg-gray-800 rounded-lg font-semibold opacity-60 cursor-not-allowed"
                title="Il faut être entre 4 et 6 joueurs et que tout le monde soit prêt."
              >
                En attente des joueurs…
              </button>
            )}
          </div>

          {!me && (
            <p className="mt-4 text-sm text-yellow-300">
              Vous n’êtes pas encore dans cette session. Revenez via la page “Jouer”.
            </p>
          )}
        </div>
      </div>

      <ImmersiveTutorial
        open={onboardingOpen}
        onClose={() => setOnboardingOpen(false)}
        onComplete={completeOnboarding}
        steps={TUTORIAL_STEPS}
        GameInterface={FakeGameInterface}
      />

      {/* 🎰 ANIMATION TIRAGE AU SORT - EFFET BANDIT MANCHOT */}
      {showTiebreakerAnimation && finalizeResult && (
        <SlotMachineAnimation
          proposals={proposals}
          players={players}
          finalizeResult={finalizeResult}
        />
      )}

      {/* 📢 Notification résultat (autres méthodes) */}
      {finalizeResult && !showTiebreakerAnimation && finalizeResult.method !== "tiebreaker" && finalizeResult.method !== "random_pick" && (
        <div className="fixed bottom-6 right-6 z-[100] animate-slide-up">
          <div className="rounded-xl border border-indigo-400/30 bg-slate-950 px-4 py-3 shadow-2xl max-w-sm">
            <div className="flex items-start gap-3">
              <span className="text-2xl">
                {finalizeResult.method === "vote_winner" && "🏆"}
                {finalizeResult.method === "random_pick" && "🎲"}
                {finalizeResult.method === "first_proposal" && "✨"}
                {finalizeResult.method === "auto_generated" && "🤖"}
              </span>
              <div>
                <p className="text-sm font-semibold text-white">
                  {finalizeResult.method === "vote_winner" && "Nom le plus voté !"}
                  {finalizeResult.method === "random_pick" && "Tirage aléatoire !"}
                  {finalizeResult.method === "first_proposal" && "Premier proposé !"}
                  {finalizeResult.method === "auto_generated" && "Nom auto-généré !"}
                </p>
                <p className="text-xs text-slate-300 mt-1">
                  Nom d'équipe : <span className="font-bold text-indigo-200">{finalizeResult.teamName}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Animation CSS pour le toast */}
      <style>{`
  @keyframes slide-up {
    from {
      transform: translateY(100px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  .animate-slide-up {
    animation: slide-up 0.4s ease-out;
  }
`}</style>

    </div>
  );
}

export default WaitingRoom;
